import React, { FC, useEffect, useContext } from 'react';
import _ from 'lodash';
import { Skeleton } from 'antd';
import { formatNumWithKMBAlt, getConfig, getMaxBound, getMinBound, removeFunctionFromProps } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import useTrackTabChange from 'hooks/general/useTrackTabChange';
const G2 = require('@antv/g2');

const defaultLabelWordLimit = 5;
//ADD ALL THE REQUIRED PROPS AT TOP
interface Props {
    fields: Array<string>;
    dataset: Array<any>;
    chartContainer: string;
    height: string | number;
    loading: boolean;
    colorKey: string;

    showLoader?: boolean;
    yAxisConfig?: boolean | object;
    xAxisConfig?: boolean | object;
    chartConfig?: object;
    yAxisLabelFormatter?: (val: any) => any;
    xAxisLabelFormatter?: (val: any) => any;
    tooltipConfig?: object;
    YGrid?: boolean;
    XGrid?: boolean;
    padding?: [number, number, number, number];
    width?: number;
    barWidth?: number;
    yAxisTitle?: string;
    xAxisTitle?: string;
    error?: any;
    additionalStyle?: any;
    minBound?: number;
    maxBound?: number;
    showLegend?: boolean;

    showErrorBox?: boolean;
    transpose?: boolean;
    Yline?: boolean;
    Xline?: boolean;

    enablePointChart?: boolean;
    groupedChartColor?: [string, string];

    intervalStyle?: object;
    disableIntervalLabel?: boolean;
    tooltipFormatter?: (xAxis: any, val: any, from: any) => any;
    pointShape?: string;
    additionalClass?: string;
}

const Linechart: FC<Props> = ({
    yAxisConfig = true,
    xAxisConfig = true,
    chartConfig = {},
    height = 400,
    width,
    fields,
    xAxisTitle = '',
    yAxisTitle = '',
    colorKey = 'color',
    additionalStyle,
    dataset,
    chartContainer,
    tooltipConfig = {},
    loading,
    padding = [0, 0, 0, 0],
    error,
    barWidth = 20,
    YGrid = true,
    XGrid = false,
    Yline = false,
    Xline = true,
    showLoader = true,
    showErrorBox = true,
    minBound,
    showLegend = false,
    yAxisLabelFormatter,
    xAxisLabelFormatter,
    additionalClass = '',

    tooltipFormatter = (xAxis, val) => {
        return {
            name: xAxis,
            value: formatNumWithKMBAlt(val, 2, 2),
        };
    },
    maxBound,
    transpose,
    enablePointChart = false,
    groupedChartColor = ['', ''],
    intervalStyle = {},
    disableIntervalLabel = false,
    pointShape = 'circle',
}) => {
    // if (!loading && error && dataset?.length === 0 && showErrorBox) {
    //     return <ErrorBox msg={String(error)} />;
    // }
    const isWindowTabActive = useTrackTabChange();
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontSizeAlt, graphLabelColorAlt, graphFontFamily } = commonSetting;
    useEffect(() => {
        if (!window) return;
        const defaultTooltipConfig = {
            showMarkers: false,
            shared: true,
            showCrosshairs: false,
            follow: true,
        };

        const min = minBound ? minBound : getMinBound(dataset, fields);
        const max = maxBound ? maxBound : getMaxBound(dataset, fields);
        const chart = new G2.Chart({
            container: chartContainer,
            height: height,
            width: width,
            autoFit: width ? false : true,
            appendPadding: padding,
        });
        chart.data(dataset);

        chart.scale(fields[1], {
            nice: true,
            min: min,
            max: max,
            ...chartConfig,
        });
        chart.scale(fields[2], {
            nice: true,
            min: min,
            max: max,
            ...chartConfig,
        });

        if (yAxisConfig) {
            chart.axis(fields[1], {
                style: {
                    textAlign: 'center',
                },
                label: !yAxisLabelFormatter
                    ? null
                    : {
                          formatter: yAxisLabelFormatter,
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                line: Yline ? {} : null,
                grid: YGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !yAxisTitle
                    ? null
                    : {
                          text: yAxisTitle,
                          position: 'middle',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
                position: 'left',
            });
        }
        if (!yAxisConfig) chart.axis(fields[1], false);
        if (!xAxisConfig) chart.axis(fields[0], false);
        if (xAxisConfig) {
            chart.axis(fields[0], {
                label: !xAxisLabelFormatter
                    ? null
                    : {
                          formatter: xAxisLabelFormatter,
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                          //   offset: 0,
                      },
                tickLine: {
                    alignTick: true,
                },
                line: Xline ? {} : null,
                grid: XGrid
                    ? {
                          line: {
                              style: {
                                  lineDash: [3, 3],
                              },
                          },
                      }
                    : null,
                title: !xAxisTitle
                    ? null
                    : {
                          text: xAxisTitle,
                          position: 'start',
                          style: {
                              fontSize: graphFontSize,
                              fill: graphLabelColor,
                              fontFamily: graphFontFamily,
                          },
                      },
            });
        }

        if (enablePointChart) {
            chart
                .point()
                .position(`${fields[0]}*${fields[1]}`)
                .shape(pointShape)
                .color(groupedChartColor[0])
                .label(`${fields[1]}`, (val: number) => {
                    if (disableIntervalLabel) return;
                    const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                    return {
                        position: pos,
                        content: val === 0 ? null : formatNumWithKMBAlt(val, 2, 2),
                        style: {
                            fontSize: graphFontSizeAlt,
                            fill: graphLabelColorAlt,
                            fontFamily: graphFontFamily,
                        },
                    };
                })
                .tooltip(false);
        }
        chart
            .line()
            .position(`${fields[0]}*${fields[1]}`)
            .color(groupedChartColor[0])
            .label(`${fields[1]}`, (val: number) => {
                if (disableIntervalLabel) return;
                const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                return {
                    position: pos,
                    content: val === 0 ? null : formatNumWithKMBAlt(val, 2, 2),
                };
            })
            .tooltip(`${fields[0]}*${fields[1]}`, (xAxis: any, val: any) => tooltipFormatter(xAxis, val, fields[1]))
            .style({ ...intervalStyle });

        if (fields.length > 2) {
            if (enablePointChart) {
                chart
                    .point()
                    .position(`${fields[0]}*${fields[2]}`)
                    .shape(pointShape)
                    .color(groupedChartColor[1])
                    .label(`${fields[2]}`, (val: number) => {
                        if (disableIntervalLabel) return;
                        const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                        return {
                            position: pos,
                            content: val === 0 ? null : formatNumWithKMBAlt(val, 2, 2),
                            style: {
                                fontSize: graphFontSizeAlt,
                                fill: graphLabelColorAlt,
                                fontFamily: graphFontFamily,
                            },
                        };
                    })
                    .tooltip(false);
            }
            // chart.axis(fields[2], {
            //     style: {
            //         textAlign: 'center',
            //     },
            //     label: !yAxisLabelFormatter
            //         ? null
            //         : {
            //               formatter: yAxisLabelFormatter,
            //           },
            //     line: Yline ? {} : null,
            //     grid: YGrid
            //         ? {
            //               line: {
            //                   style: {
            //                       lineDash: [3, 3],
            //                   },
            //               },
            //           }
            //         : null,
            //     title: !yAxisTitle
            //         ? null
            //         : {
            //               text: yAxisTitle,
            //               position: 'middle',
            //           },
            //     position: 'left',
            // });
            chart.axis(fields[2], false);
            chart
                .line()
                .position(`${fields[0]}*${fields[2]}`)
                .color(groupedChartColor[1])
                .label(`${fields[2]}`, (val: number) => {
                    if (disableIntervalLabel) return;
                    const pos = val > 0 ? (transpose ? 'end' : 'top') : transpose ? 'start' : 'bottom';
                    return {
                        position: pos,
                        content: val === 0 ? null : formatNumWithKMBAlt(val, 2, 2),
                        style: {
                            fontSize: graphFontSizeAlt,
                            fill: graphLabelColorAlt,
                            fontFamily: graphFontFamily,
                        },
                    };
                })
                .tooltip(`${fields[0]}*${fields[2]}`, (xAxis: any, val: any) => tooltipFormatter(xAxis, val, fields[2]))
                .style({ ...intervalStyle });
        }

        if (transpose) chart.coordinate().transpose();

        //just for tooltip

        chart.tooltip({
            ...defaultTooltipConfig,
            ...tooltipConfig,
            containerTpl:
                '<div class="g2-tooltip" style="min-width:200px;">' +
                '<div class="g2-tooltip-title" style="margin:10px 0;"></div>' +
                '<ul class="g2-tooltip-list" style="font-size:11px;"></ul></div>',
        });

        if (!showLegend) chart.legend(false);
        else
            chart.legend({
                position: 'top',
                layout: 'horizontal',
            });

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [dataset, isWindowTabActive]);

    return (
        <div className="">
            {showLoader && (
                <>
                    <Skeleton loading={loading} />
                    <Skeleton loading={loading} />
                </>
            )}
            <div
                style={{ opacity: loading ? 0.3 : 1, ...additionalStyle }}
                id={chartContainer}
                className={additionalClass}
            />
        </div>
    );
};

function arePropsEqual(prevProps: any, nextProps: any) {
    const [clonePrev, cloneNext] = removeFunctionFromProps({ ...prevProps }, { ...nextProps });
    return _.isEqual(clonePrev, cloneNext);
}

export default React.memo(Linechart, arePropsEqual);
